import LogRocket from 'logrocket';

import { Logger } from '@cowbell/utils';

import type { AnalyticsEventMap } from './analytics-event-map.types';

const SESSION_URL_TIMEOUT_MS = 5000;

/** Options passed to {@link Analytics} at construction (e.g. from `AnalyticsProvider`). */
interface AnalyticsOptions {
  debug?: boolean; // enable logging to console
  enabled?: boolean; // enable sending events to LogRocket
}

/**
 * Property bag forwarded to LogRocket. Keeps payloads compatible with its typed track API
 * while allowing primitive arrays and undefined values.
 */
type TrackEventProperties = {
  [key: string]:
    | string
    | number
    | boolean
    | string[]
    | number[]
    | boolean[]
    | undefined;
};

/**
 * Application analytics facade: user identification and custom events, currently backed by LogRocket.
 *
 * Intended use: a single instance is supplied through React context (console `AnalyticsProvider` /
 * `useAnalytics`). Construct with `{ debug: true }` during development to avoid sending events
 * to LogRocket while still inspecting calls in verbose logs.
 */
export class Analytics {
  private readonly logger: Logger;
  private readonly enabled: boolean;

  /**
   * @param options - `debug` skips LogRocket and routes calls through the scoped `Logger`.
   */
  constructor(options: AnalyticsOptions = {}) {
    this.logger = new Logger({ scope: 'Analytics', enabled: options.debug });
    this.enabled = options.enabled ?? false;

    if (!this.enabled) {
      this.logger.warn('Analytics are disabled');
    }

    this.logger.log(
      'Analytics initialized in debug mode: Check your console\'s "Verbose" tab for more details.'
    );

    return this;
  }

  init(appId: string, options: Record<string, unknown>) {
    this.logger.debug('Initialized', { appId });

    if (this.enabled) {
      LogRocket.init(appId, { dom: options });
    }
  }

  /**
   * Associates the current session with a user (display name and email for support tooling).
   *
   * In **debug** mode, emits a structured debug log only. When not in debug mode, the current
   * implementation is a no-op; wire `LogRocket.identify` here when ready for production.
   *
   * @param id - Stable user identifier (e.g. auth subject or internal user id).
   * @param userData - Human-readable fields passed to the analytics backend.
   */
  identify(id: string, userData: { name: string; email: string }) {
    this.logger.debug('User Identified', { id, userData });

    if (this.enabled) {
      LogRocket.identify(id, userData);
    }
  }

  /**
   * Records a registered product event with a payload typed from {@link AnalyticsEventMap}.
   *
   * In **debug** mode, logs the event and does not call LogRocket. Otherwise forwards to
   * `LogRocket.track` (see LogRocket SDK docs for custom events).
   *
   * @param eventName - Must be a key from the central analytics event map (e.g. via `QuoteAnalyticsEvents`).
   * @param data - Properties for that event (base actor/persona fields plus event-specific fields from the map).
   */
  track<K extends keyof AnalyticsEventMap>(
    eventName: K,
    data: AnalyticsEventMap[K]
  ) {
    this.deliverTrackEvent(eventName, data);
  }

  /**
   * Retrieves the current session URL and passes it to the callback for logging.
   *
   * In debug mode, delegates directly to LogRocket (for parity with future production behavior).
   *
   * @param callback - Function that receives the session URL as a string.
   * @returns The value returned by LogRocket.getSessionURL (typically void).
   */
  async getSessionURL(): Promise<string> {
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error('Timed out resolving LogRocket session URL'));
      }, SESSION_URL_TIMEOUT_MS);

      LogRocket.getSessionURL((sessionURL) => {
        clearTimeout(timeoutId);
        resolve(sessionURL);
      });
    });
  }

  /**
   * Returns the current LogRocket session URL synchronously, or an empty string
   * if no session is active yet. Use for per-event reads (e.g. Sentry `beforeSend`)
   * so events always carry the live session rather than a stale snapshot.
   */
  getCurrentSessionURL(): string {
    return LogRocket.sessionURL ?? '';
  }

  private deliverTrackEvent(eventName: string, data: TrackEventProperties) {
    this.logger.debug(`track: ${eventName}`, { data });

    if (this.enabled) {
      LogRocket.track(eventName, data);
    }
  }
}
